import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AffordableFood from "./InfoBoxes/AffordableFood";
import AnimalDeathInfo from "./InfoBoxes/AnimalDeathInfo";
import EnvironmentInfo from "./InfoBoxes/EnvironmentInfo";
import HealthImpact from "./InfoBoxes/HealthImpact";
import ProSports from "./InfoBoxes/ProSports";
import Religion from "./InfoBoxes/Religion";
import "./InfoSheet.css";

const FACTS = [
  {
    id: "animals",
    title: "Animal Deaths",
    component: AnimalDeathInfo,
    textContent:
      "Each year, 70+ billion land animals and trillions of marine animals are slaughtered globally. A vegan diet saves around 200 animals annually.",
  },
  {
    id: "environment",
    title: "Environmental Impact",
    component: EnvironmentInfo,
    textContent:
      "Animal agriculture causes 18% of greenhouse emissions, plus deforestation and water pollution. Vegan diets require only 1/3 of the land needed for meat.",
  },
  {
    id: "health",
    title: "Health Benefits",
    component: HealthImpact,
    textContent:
      "Plant-based diets reduce heart disease, diabetes, and cancer risks. Vegans typically have lower BMI, cholesterol, and blood pressure than meat-eaters.",
  },
  {
    id: "sports",
    title: "Pro Sports",
    component: ProSports,
    textContent:
      "Elite athletes like Venus Williams and Lewis Hamilton embrace plant-based diets for enhanced performance and faster recovery.",
  },
  {
    id: "religion",
    title: "Religion",
    component: Religion,
    textContent:
      "Many spiritual traditions embrace compassion toward animals. Buddhism and Jainism emphasize non-violence, while many monasteries observe meat-free practices.",
  },
  {
    id: "food",
    title: "Affordable Food",
    component: AffordableFood,
    textContent:
      "Vegan diets can be budget-friendly. Staples like rice, beans, lentils, and vegetables are affordable. Studies show vegetarians save $750 annually.",
  },
];

// Function to truncate text to a certain number of words
const truncateText = (text, wordLimit = 50) => {
  if (!text) return "";
  const words = text.split(" ");
  if (words.length <= wordLimit) return text;
  return words.slice(0, wordLimit).join(" ") + "...";
};

const Modal = ({ isOpen, onClose, title, children }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay">
      <div className="modal-content" ref={modalRef}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

const InfoCard = ({ item, isEven, onCardClick }) => {
  // Get truncated version of the text content
  const truncatedInfo = truncateText(item.textContent);

  const handleSeeMoreClick = (e) => {
    e.stopPropagation(); // Prevent card click from triggering
    onCardClick(item);
  };

  return (
    <div
      className={`info-card ${isEven ? "even" : "odd"}`}
      onClick={() => onCardClick(item)}
    >
      <div className="info-text">
        <h3 className="info-card-title">{item.title}</h3>
        <div className="text-content">{truncatedInfo}</div>
        <button className="see-more-btn" onClick={handleSeeMoreClick}>
          SEE MORE
        </button>
      </div>
      <div className="info-image">{item.component.image()}</div>
    </div>
  );
};

const InfoSheet = () => {
  const [selectedCard, setSelectedCard] = useState(null);

  const handleCardClick = (item) => {
    setSelectedCard(item);
  };

  const handleCloseModal = () => {
    setSelectedCard(null);
  };

  return (
    <div className="info-sheet-container">
      {FACTS.map((fact, index) => (
        <InfoCard
          key={fact.id}
          item={fact}
          isEven={index % 2 === 0}
          onCardClick={handleCardClick}
        />
      ))}

      <Modal
        isOpen={selectedCard !== null}
        onClose={handleCloseModal}
        title={selectedCard?.title || ""}
      >
        {selectedCard && (
          <div>
            <div className="modal-info-image">
              {selectedCard.component.image()}
            </div>
            <div className="modal-content-wrapper">
              <div className="modal-info-text">
                {selectedCard.component.info()}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InfoSheet;
